// TODO(boundaries) Fix after modernizing architecture
/* eslint-disable @nx/enforce-module-boundaries */
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { ErrorHandler, LOCALE_ID, enableProdMode, importProvidersFrom } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { TitleStrategy, provideRouter } from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import 'hammerjs';
import { provideQuillConfig } from 'ngx-quill';

import { provideQueryClient } from '@core/shared/data-access';
import {
  AbstractThemeChangesTrackingService,
  GlobalErrorHandlerService,
  InsightsEventsTrackingService,
  ThemeChangesTrackingDevService,
  provideCustomSvgIconRegistry,
} from '@core/shared/util';
import { provideExportFeature } from '@mp/content-manager/exports/feature';
import { NewsPermissions } from '@mp/dashboard/newsfeed/feature';
import { provideAddressCostCentersFeature } from '@mp/organization-master-data/addresses/address-cost-centers/feature';
import { AppInsightsFacade } from '@mp/shared/app-insights/data-access';
import { provideAppInsightsFeature } from '@mp/shared/app-insights/feature';
import { InsightsOrganizationInfoService } from '@mp/shared/app-insights/util';
import { HttpErrorInterceptor, PlatformRouterSerializer, provideSharedDataAccess } from '@mp/shared/data-access';
import { JobServerPermissions, ObservabilityPermissions } from '@mp/shared/feature';
import { AbstractLinkProviderService, provideModuleInfo } from '@mp/shared/util';
import { DeploymentSettingsPermissions } from '@mp/system/deployment-settings/feature';
import { provideMaintenanceDataAccess } from '@mp/system/maintenance/data-access';
import { provideMaintenanceFeature } from '@mp/system/maintenance/feature';
import { PlatformPackagingUnitsPermissions } from '@mp/system/platform-packaging-units/feature';
import { ActiveOrganizationInterceptor, provideProfileDataAccess } from '@mpauth/shared/data-access';
import { provideBusinessIntelligenceFeature } from '@mpbi/feature';
import { provideContentManagerFeature } from '@mpcm/feature';
import { provideDocumentManagementFeature } from '@mpdm/feature';
import { provideAmor3Feature } from '@mpia/feature';
import { provideEcosioFeature } from '@mpie/feature';
import { provideGhxFeature } from '@mpig/feature';
import { provideMediioFeature } from '@mpim/feature';
import { provideSapOdataFeature } from '@mpiso/feature';
import { provideKernelFeature } from '@mpk/feature';
import { AbstractAuthInfoProviderService, provideModulePermissions } from '@mpk/shared/data-access';
import { provideMedicalShopFeature } from '@mpms/feature';
import { provideOrganizationMasterDataFeature } from '@mpomd/feature';
import { provideSupplierManagementFeature } from '@mpsm/feature';
import { provideClinicSupplierAddressCodesFeature } from '@mpsm/shared/feature';
import { provideTransactionManagementFeature } from '@mptm/feature';

import { AppComponent } from './app/app.component';
import { REDUCERS_TOKEN, reducerProvider } from './app/app.reducer';
import { routes } from './app/app.routes';
import { AppInsightsOrganizationInfoService, AuthInfoProviderService } from './app/auth';
import { CUSTOM_DATE_FORMATS, CustomDatePickerAdapter } from './app/date-adapter';
import { AppTitleStrategy } from './app/info/app-title-strategy';
import { appInitProvider } from './app/init/app-init.service';
import { LinkProviderService } from './app/navigation';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode();
}

registerLocaleData(localeDe, 'de', localeDeExtra);

bootstrapApplication(AppComponent, {
  providers: [
    // Angular providers
    importProvidersFrom(BrowserModule),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: LOCALE_ID, useValue: 'de-DE' },
    { provide: TitleStrategy, useClass: AppTitleStrategy },
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ActiveOrganizationInterceptor,
      multi: true,
    },

    // Material providers
    { provide: DateAdapter, useClass: CustomDatePickerAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    provideCustomSvgIconRegistry(),

    // Ngrx state management providers
    provideStore(REDUCERS_TOKEN, {
      runtimeChecks: {
        strictActionImmutability: true,
        strictStateImmutability: true,
      },
    }),
    reducerProvider,
    provideEffects(),
    provideRouterStore({ serializer: PlatformRouterSerializer }),
    !environment.production ? provideStoreDevtools({ connectInZone: true }) : [],

    provideQueryClient(),

    provideQuillConfig({}),

    provideAppInsightsFeature(),

    // App specific providers
    appInitProvider,
    provideSharedDataAccess(),
    provideProfileDataAccess(),
    provideMaintenanceDataAccess(),
    /**
     * Die Adressen gehören der Routenkonfiguration dieser Anwendung: sie werden dem Wartungsmodus von hier
     * mitgegeben, damit ein umbenannter Pfad nicht still die Sperre über der Organisationsauswahl liegen
     * lässt - dort wäre sie eine Sackgasse mit Abmelden als einziger Handlung.
     *
     * /system/deployment-settings/maintenance ist aus demselben Grund dabei: die serverseitige Ausnahme
     * ([AllowDuringMaintenance] auf Read-/SaveMaintenanceSettingsController) prüft nur, ob der Request
     * durchkommt - die Sperrmaske hier prüft unabhängig davon `isExemptForCurrentUser` und deckte diese
     * Route nicht ab. Ein Benutzer mit ManageMaintenanceSettings, aber ohne SysAdmin/administrative Rolle,
     * käme sonst nie bis zum freigegebenen Speichern-Knopf - die Vollbild-Sperre läge im Weg, bevor die
     * Ausnahme am Endpunkt überhaupt zum Tragen kommt.
     */
    provideMaintenanceFeature({
      organizationSelectionUrl: '/active-organization-selection',
      additionalUnlockedUrls: ['/no-organization', '/system/deployment-settings/maintenance'],
    }),
    provideKernelFeature(),
    provideOrganizationMasterDataFeature(),
    provideBusinessIntelligenceFeature(),
    provideContentManagerFeature(),
    provideSupplierManagementFeature(),
    provideMedicalShopFeature(),
    provideDocumentManagementFeature(),
    provideTransactionManagementFeature(),
    provideAddressCostCentersFeature(),
    provideClinicSupplierAddressCodesFeature(),
    provideExportFeature(),
    provideModuleInfo('Jobs', {
      displayName: 'Jobs',
    }),
    provideModulePermissions('Jobs', JobServerPermissions),
    provideModuleInfo('Observability', {
      displayName: 'Logging',
    }),
    provideModulePermissions('Observability', ObservabilityPermissions),
    provideModulePermissions('Kernel', NewsPermissions, DeploymentSettingsPermissions),
    provideModulePermissions('ContentManager', PlatformPackagingUnitsPermissions),
    provideAmor3Feature(),
    provideEcosioFeature(),
    provideSapOdataFeature(),
    provideGhxFeature(),
    provideMediioFeature(),

    { provide: AbstractAuthInfoProviderService, useClass: AuthInfoProviderService },
    !environment.production
      ? { provide: AbstractThemeChangesTrackingService, useClass: ThemeChangesTrackingDevService }
      : [],
    { provide: AbstractLinkProviderService, useClass: LinkProviderService },
    { provide: InsightsEventsTrackingService, useExisting: AppInsightsFacade },
    { provide: InsightsOrganizationInfoService, useClass: AppInsightsOrganizationInfoService },
  ],
}).catch((err) => console.error(err));
